import { Link } from 'react-router';
import { SITE_URL } from '../content/types';
import { PageMeta } from '../components/PageMeta';
import { MarketingHeader } from '../components/marketing/MarketingHeader';
import { MarketingFooter } from '../components/MarketingFooter';
import { ContentCTA } from '../components/content/ContentCTA';

const values = [
  {
    title: 'Privacy-first',
    body: 'Built by a healthcare company, Pollenate treats data carefully by default — with a HIPAA-ready architecture, encryption options, and a BAA for regulated teams.',
  },
  {
    title: 'Developer-friendly',
    body: 'A single script tag, a typed React package, a full REST API, and an MCP server. Pollenate is designed to drop into real products in minutes.',
  },
  {
    title: 'Transparent',
    body: 'Public, predictable pricing with a genuine free tier and no per-seat fees. You always know what you pay and what you get.',
  },
];

const stats = [
  { value: 'Free', label: 'Forever plan' },
  { value: '6', label: 'Widget types' },
  { value: '<50ms', label: 'Global edge response' },
  { value: '99.9%', label: 'Uptime target' },
];

/** Company / about page — establishes entity identity and E-E-A-T signals. */
export function AboutPage() {
  const url = `${SITE_URL}/about`;

  const aboutLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About Pollenate',
    url,
    mainEntity: {
      '@type': 'Organization',
      name: 'Pollenate',
      url: SITE_URL,
      logo: `${SITE_URL}/favicon-light.svg`,
      description:
        'Pollenate is an embeddable customer feedback platform — widgets, feedback pages, NPS, CSAT, and AI-powered insights.',
      sameAs: ['https://x.com/PollenateCloud', 'https://linkedin.com/company/pollenate-feedback/'],
      parentOrganization: { '@type': 'Organization', name: 'BlueHive Health', url: 'https://bluehive.com' },
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'About', item: url },
    ],
  };

  return (
    <div className="min-h-screen bg-[#fafafa] dark:bg-neutral-950">
      <PageMeta
        title="About Pollenate — Customer Feedback, Built by a Healthcare Company"
        description="Pollenate is an embeddable customer feedback platform from BlueHive Health. Learn about our mission, values, and privacy-first approach to collecting feedback."
        url={url}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutLd).replace(/</g, '\\u003c') }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd).replace(/</g, '\\u003c') }} />

      <MarketingHeader />

      <main id="main-content" className="px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="max-w-3xl mx-auto">
          <p className="text-sm font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">About</p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
            Feedback that teams actually act on
          </h1>
          <p className="mt-5 text-lg text-slate-600 dark:text-neutral-400">
            Pollenate is an embeddable customer feedback platform. We help teams collect ratings, comments, NPS,
            and CSAT directly from their websites, apps, and documentation — then turn that signal into action with
            real-time dashboards and AI-powered search.
          </p>

          <div className="prose prose-slate dark:prose-invert max-w-none mt-8">
            <h2>Who builds Pollenate?</h2>
            <p>
              Pollenate is built by{' '}
              <a href="https://bluehive.com?utm_source=pollenate&utm_medium=website&utm_campaign=about" target="_blank" rel="noopener noreferrer">
                BlueHive Health
              </a>
              , a healthcare technology company. Because our roots are in healthcare, privacy and security aren&rsquo;t
              afterthoughts — they&rsquo;re built in. That&rsquo;s why Pollenate ships with a HIPAA-ready architecture
              and a BAA option that most feedback tools simply don&rsquo;t offer.
            </p>
            <h2>Why we built it</h2>
            <p>
              Most feedback tools force a trade-off: simple but shallow, or powerful but painful. We wanted something a
              developer could add in one line, a product manager could read at a glance, and a regulated team could
              trust with sensitive data. Pollenate is that middle path — lightweight to adopt, serious where it counts.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {stats.map((s) => (
              <div key={s.label} className="rounded-xl border border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4 text-center">
                <div className="text-2xl font-bold text-slate-900 dark:text-white">{s.value}</div>
                <div className="mt-1 text-xs text-slate-500 dark:text-neutral-400">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            {values.map((v) => (
              <div key={v.title} className="rounded-xl border border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-6">
                <h2 className="text-base font-bold text-slate-900 dark:text-white">{v.title}</h2>
                <p className="mt-2 text-sm text-slate-600 dark:text-neutral-400">{v.body}</p>
              </div>
            ))}
          </div>

          <p className="mt-10 text-slate-600 dark:text-neutral-400">
            Want to see it in action?{' '}
            <Link to="/docs" className="text-amber-600 dark:text-amber-400 hover:underline">
              Read the docs
            </Link>{' '}
            or{' '}
            <Link to="/pricing" className="text-amber-600 dark:text-amber-400 hover:underline">
              compare plans
            </Link>
            .
          </p>
        </div>
      </main>

      <ContentCTA />
      <MarketingFooter />
    </div>
  );
}
